/**
 * WriteoffSlice Selectors
 * 
 * Memoized selectors for accessing writeoff slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { WriteoffSliceState } from './state';

// Base selector to get the writeoff slice
const selectWriteoffSlice = (state: RootState): WriteoffSliceState => state.writeoff;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current writeoff entity
 */
export const selectCurrentWriteoff = createSelector(
  selectWriteoffSlice,
  (slice) => slice.currentWriteoff
);

/**
 * Select whether current writeoff exists
 */
export const selectHasCurrentWriteoff = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff !== null
);

/**
 * Select the writeoff ID (if exists)
 */
export const selectWriteoffId = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.identifiers?.id
);

// =============================================================================
// EDIT TRACKING SELECTORS
// =============================================================================

/**
 * Select whether any edits have been made
 */
export const selectWriteoffIsEdited = createSelector(
  selectWriteoffSlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectWriteoffEditedFields = createSelector(
  selectWriteoffSlice,
  (slice) => slice.editedFields
);

/**
 * Factory to create a selector for checking if a specific field is edited
 */
export const selectIsWriteoffFieldEdited = (fieldName: string) =>
  createSelector(selectWriteoffEditedFields, (fields) => fields.includes(fieldName));

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectWriteoffValidationErrors = createSelector(
  selectWriteoffSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasWriteoffValidationErrors = createSelector(
  selectWriteoffValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory to create a selector for a specific field's validation error
 */
export const selectWriteoffValidationError = (fieldName: string) =>
  createSelector(selectWriteoffValidationErrors, (errors) => errors[fieldName]);

// =============================================================================
// SUB-ENTITY SELECTORS
// =============================================================================

/**
 * Select writeoff identifiers
 */
export const selectWriteoffIdentifiers = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.identifiers
);

/**
 * Select writeoff patient information
 */
export const selectWriteoffPatient = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.patient
);

/**
 * Select writeoff detail information
 */
export const selectWriteoffDetail = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.detail
);

/**
 * Select writeoff notes
 */
export const selectWriteoffNotes = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.notes ?? []
);

/**
 * Select writeoff notes count
 */
export const selectWriteoffNotesCount = createSelector(
  selectWriteoffNotes,
  (notes) => notes.length
);

/**
 * Select writeoff approvals
 */
export const selectWriteoffApprovals = createSelector(
  selectCurrentWriteoff,
  (writeoff) => writeoff?.approvals ?? []
);

/**
 * Select writeoff approvals count
 */
export const selectWriteoffApprovalsCount = createSelector(
  selectWriteoffApprovals,
  (approvals) => approvals.length
);

// =============================================================================
// DERIVED SELECTORS
// =============================================================================

/**
 * Select computed data about the writeoff
 */
export const selectWriteoffSummary = createSelector(
  selectCurrentWriteoff,
  selectWriteoffIsEdited,
  selectWriteoffNotesCount,
  selectWriteoffApprovalsCount,
  (writeoff, isEdited, notesCount, approvalsCount) => ({
    id: writeoff?.identifiers?.id,
    caseId: writeoff?.identifiers?.caseId,
    patientName: writeoff?.patient
      ? `${writeoff.patient.lastName}, ${writeoff.patient.firstName}`
      : undefined,
    isEdited,
    notesCount,
    approvalsCount,
  })
);
